import moment from 'moment-timezone';
import {
  SUSPENDED_DUE_PAYMENT_OPTIONS,
  SuspendedDuePaymentOption,
} from '../constants/enums';
import {
  GetNewBillingInfoBillingPeriod,
  GetNewBillingInfoResponse,
  SubscriptionPlan,
} from '../types';
import { formatCurrency, formatDate, roundDecimal } from '@/core/lib/utils';

export interface SuspendedDuePaymentDialogData {
  outstandingBalance: number;
  reactivateTotalCharges: number;
  continueTotalCharges: number;
  currentBillingPeriod: string;
  continueBillingPeriod: string;
  showReactivateOption: boolean;
  defaultOption: SuspendedDuePaymentOption;
}

const formatBillingPeriod = (
  startDate: Date | string | undefined | null,
  endDate: Date | string | undefined | null
) => {
  if (!startDate || !endDate) return '';
  return `${formatDate(startDate, 'MMM D, YYYY')} to ${formatDate(endDate, 'MMM D, YYYY')}`;
};

export const isSuspendedPlanBillingCycleEnded = (
  plan: SubscriptionPlan
): boolean => {
  const endDate = moment(plan.BillingInfo.NextBillingDate)
    .subtract(1, 'day')
    .startOf('day')
    .toDate();
  if (!endDate) {
    return true;
  }
  return moment(endDate).endOf('day').isBefore(moment().startOf('day'));
};

export const getDefaultSuspendedDuePaymentOption = (
  plan: SubscriptionPlan
): SuspendedDuePaymentOption => {
  if (isSuspendedPlanBillingCycleEnded(plan)) {
    return SUSPENDED_DUE_PAYMENT_OPTIONS.CONTINUE_WITH_BALANCE;
  }
  return SUSPENDED_DUE_PAYMENT_OPTIONS.REACTIVATE;
};

/** Builds dialog display/payment data from `/v4GetNewBillingInfo` response only. */
export const getSuspendedDuePaymentDialogDataFromApi = (
  api: GetNewBillingInfoResponse,
  plan: SubscriptionPlan
): SuspendedDuePaymentDialogData => {
  return {
    outstandingBalance: api.balance,
    reactivateTotalCharges: api.reactivateTotalCharges,
    continueTotalCharges: api.totalCharges,
    currentBillingPeriod: formatBillingPeriodFromApi(api.currentBillingPeriod),
    continueBillingPeriod: formatBillingPeriodFromApi(api.newBillingPeriod),
    showReactivateOption: !isSuspendedPlanBillingCycleEnded(plan),
    defaultOption: getDefaultSuspendedDuePaymentOption(plan),
  };
};

export const getSuspendedDuePaymentAmount = (
  option: SuspendedDuePaymentOption,
  billing: GetNewBillingInfoResponse
): number => {
  switch (option) {
    case SUSPENDED_DUE_PAYMENT_OPTIONS.REACTIVATE:
      return roundDecimal(billing.reactivateTotalCharges, 2);
    case SUSPENDED_DUE_PAYMENT_OPTIONS.CONTINUE_WITH_BALANCE:
      return roundDecimal(billing.balance, 2);
    case SUSPENDED_DUE_PAYMENT_OPTIONS.SELECT_NEW_PLAN:
      return roundDecimal(billing.balance, 2);
    default:
      return roundDecimal(billing.balance, 2);
  }
};

export const formatSuspendedDueCurrency = (amount: number) =>
  formatCurrency(amount);

/** Redux state must only hold serializable date strings, not Date instances. */
const toSerializableDateString = (
  value: Date | string | undefined | null
): string | null => {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  if (value instanceof Date) return value.toISOString();
  return null;
};

const getBillingPeriodDates = (
  billingPeriod?: Record<string, unknown> | null
): GetNewBillingInfoBillingPeriod | null => {
  if (!billingPeriod) return null;

  const startRaw = (billingPeriod.startDate ??
    billingPeriod.StartDate ??
    billingPeriod.StartBillingDate) as Date | string | undefined;

  let endRaw = (billingPeriod.endDate ?? billingPeriod.EndDate) as
    | Date
    | string
    | undefined;

  const nextBillingDate = (billingPeriod.nextBillingDate ??
    billingPeriod.NextBillingDate) as Date | string | undefined;

  if (!endRaw && nextBillingDate) {
    endRaw = moment(nextBillingDate).subtract(1, 'day').toISOString();
  }

  const startDate = toSerializableDateString(startRaw);
  const endDate = toSerializableDateString(endRaw);

  if (!startDate || !endDate) return null;
  return { startDate, endDate };
};

export const formatBillingPeriodFromApi = (
  billingPeriod?: GetNewBillingInfoBillingPeriod | null
): string => {
  if (!billingPeriod?.startDate || !billingPeriod?.endDate) return '';
  return formatBillingPeriod(billingPeriod.startDate, billingPeriod.endDate);
};

/** @deprecated Use formatBillingPeriodFromApi */
export const formatNewBillingPeriodFromApi = formatBillingPeriodFromApi;

const parseNumber = (value: unknown): number | null => {
  if (value == null) return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

const parseNewBillingInfoSource = (
  source: Record<string, unknown>
): GetNewBillingInfoResponse | null => {
  const balance = parseNumber(source.balance ?? source.Balance);
  const reactivateTotalCharges = parseNumber(
    source.reactivateTotalCharges ?? source.ReactivateTotalCharges
  );
  const totalCharges = parseNumber(
    source.newTotalCharges ??
      source.NewTotalCharges ??
      source.totalCharges ??
      source.TotalCharges
  );

  const currentBillingPeriod = getBillingPeriodDates(
    (source.currentBillingInfo ??
      source.CurrentBillingInfo ??
      source.currentBillingPeriod ??
      source.CurrentBillingPeriod) as Record<string, unknown> | undefined
  );

  const newBillingPeriod = getBillingPeriodDates(
    (source.newBillingInfo ??
      source.NewBillingInfo ??
      source.newBillingPeriod ??
      source.NewBillingPeriod ??
      source.billingInfo ??
      source.BillingInfo) as Record<string, unknown> | undefined
  );

  if (
    balance == null ||
    reactivateTotalCharges == null ||
    totalCharges == null
  ) {
    return null;
  }

  return {
    balance,
    reactivateTotalCharges,
    totalCharges,
    ...(currentBillingPeriod && { currentBillingPeriod }),
    ...(newBillingPeriod && { newBillingPeriod }),
  };
};

export const isNewBillingInfoApiSuccess = (raw: unknown): boolean => {
  if (!raw || typeof raw !== 'object') return false;
  const data = raw as Record<string, unknown>;
  if (data.Success === true || data.success === true) return true;
  return data.errorCode === 0;
};

const isFullBillingInfoPayload = (data: Record<string, unknown>): boolean =>
  data.Balance != null ||
  data.balance != null ||
  data.ReactivateTotalCharges != null ||
  data.reactivateTotalCharges != null ||
  data.NewTotalCharges != null ||
  data.newTotalCharges != null;

/** Maps API payload to frontend billing info shape. */
export const normalizeNewBillingInfoResponse = (
  raw: unknown
): GetNewBillingInfoResponse | null => {
  if (raw == null || typeof raw !== 'object') return null;

  const data = raw as Record<string, unknown>;

  if (isFullBillingInfoPayload(data)) {
    return parseNewBillingInfoSource(data);
  }

  const nestedKeys = [
    'data',
    'Data',
    'context',
    'Context',
    'result',
    'Result',
  ] as const;

  for (const key of nestedKeys) {
    const child = data[key];
    if (child == null) continue;
    const normalized = normalizeNewBillingInfoResponse(child);
    if (normalized) return normalized;
  }

  return null;
};
